import { STORE_SUBSCRIPTIONS_URL, type BillingStore } from "@shared/billingStore";

/** An i18n key plus its English default, for t(key, { defaultValue }). */
export type StoreBillingCopy = { key: string; defaultValue: string };

export type StoreBilling = {
  store: BillingStore;
  /** The store's manage-subscriptions page. Open it in a new tab. */
  manageUrl: string;
  /** Current Plan card, under the plan name. */
  billedThrough: StoreBillingCopy;
  /** Current Plan card link, in place of the Stripe "Manage Billing". */
  manage: StoreBillingCopy;
  /** Bottom line, in place of "Cancel subscription" / "Edit billing". */
  note: StoreBillingCopy;
  /** The link that follows `note`. */
  cancel: StoreBillingCopy;
};

/**
 * The plan pickers (Subscription page grid, upgrade modal) while the store
 * still runs the plan: they show `note` and the `manage` link instead of
 * starting a web checkout, which would bill the plan twice.
 */
export type StorePlanLock = {
  store: BillingStore;
  manageUrl: string;
  /** Above the plan cards. */
  note: StoreBillingCopy;
  /** The link that follows `note`. */
  manage: StoreBillingCopy;
};

type CopyName =
  | Exclude<keyof StoreBilling, "store" | "manageUrl">
  | "plansNote";

const KEY_PREFIX: Record<BillingStore, string> = {
  google_play: "subscription.storeBilling.googlePlay",
  app_store: "subscription.storeBilling.appStore",
};

/** English defaults; the translations live in client/src/locales/*.ts. */
const ENGLISH: Record<BillingStore, Record<CopyName, string>> = {
  google_play: {
    billedThrough: "Billed through Google Play",
    manage: "Manage in Google Play",
    note: "Your plan was bought in the Solmi app, so Google Play manages billing.",
    cancel: "Cancel or change it in Google Play",
    plansNote:
      "Your plan is billed through Google Play, so change it in the Solmi app. To switch to web billing, cancel in Google Play first, then subscribe here once your plan ends.",
  },
  app_store: {
    billedThrough: "Billed through the App Store",
    manage: "Manage in App Store",
    note: "Your plan was bought in the Solmi app, so the App Store manages billing.",
    cancel: "Cancel or change it in the App Store",
    plansNote:
      "Your plan is billed through the App Store, so change it in the Solmi app. To switch to web billing, cancel in the App Store first, then subscribe here once your plan ends.",
  },
};

function copyFor(store: BillingStore, name: CopyName): StoreBillingCopy {
  return {
    key: `${KEY_PREFIX[store]}.${name}`,
    defaultValue: ENGLISH[store][name],
  };
}

/**
 * What the Subscription page shows for a plan bought in the iOS / Android
 * app (payment.getSubscription's `billingStore`): the store bills it, so the
 * page links to the store instead of the Stripe billing portal, which can't
 * see the plan. Null for free plans, web (Stripe) plans and unknown values,
 * which keep the Stripe buttons.
 */
export function storeBillingForPlan(
  billingStore: string | null | undefined,
  plan: string | null | undefined,
): StoreBilling | null {
  if (!plan || plan === "free") return null;
  if (billingStore !== "google_play" && billingStore !== "app_store") {
    return null;
  }
  const copy = (name: CopyName) => copyFor(billingStore, name);
  return {
    store: billingStore,
    manageUrl: STORE_SUBSCRIPTIONS_URL[billingStore],
    billedThrough: copy("billedThrough"),
    manage: copy("manage"),
    note: copy("note"),
    cancel: copy("cancel"),
  };
}

/**
 * What the plan pickers show for a plan the store still runs
 * (payment.getSubscription's `storePlanActive`): the server refuses a web
 * checkout on top of it, so the pickers say where to change the plan and how
 * to move to web billing instead. Null when a web plan can be bought, which
 * includes a store plan whose period has ended. Credit packs are never
 * locked.
 */
export function storePlanLock(
  billingStore: string | null | undefined,
  plan: string | null | undefined,
  storePlanActive: boolean | null | undefined,
): StorePlanLock | null {
  if (!storePlanActive) return null;
  const billing = storeBillingForPlan(billingStore, plan);
  if (!billing) return null;
  return {
    store: billing.store,
    manageUrl: billing.manageUrl,
    note: copyFor(billing.store, "plansNote"),
    manage: billing.manage,
  };
}
